<div class="product-selection">
  <h2 class="mb-4">{{ 'product_selection_title' | translate }}</h2>

  <!-- Loading -->
  @if (loading()) {
    <div class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'product_loading' | translate }}</span>
      </div>
    </div>
  }

  <!-- Error -->
  @if (error()) {
    <div class="alert alert-danger">
      {{ error() }}
    </div>
  }

  <!-- Initial Top-up Warning -->
  @if (!loading() && !error() && requiresTopup()) {
    <div class="alert alert-warning mb-4">
      <strong>{{ 'initial_topup_required_title' | translate }}</strong>
      <p class="mb-0">
        {{ 'initial_topup_required_message' | translate: { count: topupsRequired() } }}
      </p>
    </div>
  }

  <!-- Main Content -->
  @if (!loading() && !error()) {
    <c-row class="products-grid">
      @for (product of displayProducts(); track product.id) {
        <c-col md="4" sm="6" xs="12" class="mb-4">
          <c-card class="product-card h-100 shadow-sm">
            @if (product.photo_url) {
              <img
                [src]="product.photo_url"
                [alt]="product.name"
                cCardImg="top"
                class="product-image"
              />
            } @else {
              <div class="product-image-placeholder">
                <svg cIcon name="cilImage"></svg>
              </div>
            }

            <c-card-body class="d-flex flex-column">
              <h5 cCardTitle>{{ product.name }}</h5>

              <!-- The overview quotes the FULL price of the product. For a card that
                   is price + activation fee; the cart below charges `price` only,
                   because the fee is paid to the card partner, not here. -->
              <p class="product-price text-primary fw-bold"
                 [class.mb-3]="!product.activation_fee">
                {{ (product.total_customer_price ?? product.price) | currencyAmount: (product.currency || 'USDT') }}
              </p>
              @if (product.activation_fee) {
                <p class="product-activation-note text-body-secondary small mb-2">
                  {{ 'product_activation_fee_note' | translate: {
                       fee: (product.activation_fee | currencyAmount: (product.currency || 'USDT'))
                     } }}
                </p>
                <!-- The card partner prints an expiry date on the card and buyers read it
                     as a term, asking whether the price recurs. Said next to the price so
                     it is seen before the purchase, not only behind "Details". -->
                <div class="product-one-time-note alert alert-info small mb-3" role="note">
                  {{ 'product_one_time_payment_note' | translate: {
                       price: ((product.total_customer_price ?? product.price) | currencyAmount: (product.currency || 'USDT'))
                     } }}
                </div>
              }

              <!-- Expand/Collapse Details -->
              <button
                cButton
                color="primary"
                variant="outline"
                size="sm"
                class="mb-3"
                (click)="toggleDetails(product)"
              >
                <span>{{(isExpanded(product) ? 'product_hide_details' : 'product_details') | translate}}</span>
                <svg
                  cIcon
                  [name]="isExpanded(product) ? 'arrowUp' : 'arrowDown'"
                  size="sm"
                  class="ms-2"
                ></svg>
              </button>

              <!-- Expanded Details -->
              @if (isExpanded(product)) {
                <div class="product-details-expanded mb-3">
                  <div class="detail-content" [innerHTML]="product.description"></div>
                </div>
              }

              <!-- Quantity Selector -->
              <div class="quantity-selector mt-auto">
                <label class="form-label">{{ 'product_quantity' | translate }}</label>
                <div class="input-group">
                  <button
                    cButton
                    color="secondary"
                    variant="outline"
                    (click)="decreaseQuantity(product)"
                    [disabled]="getQuantity(product) === 0"
                  >
                    <svg cIcon name="cilMinus"></svg>
                  </button>

                  <input
                    type="number"
                    class="form-control text-center"
                    [value]="getQuantity(product)"
                    (change)="setQuantity(product, $event)"
                    min="0"
                    [attr.max]="getMaxQuantity(product)"
                  />

                  <button
                    cButton
                    color="secondary"
                    variant="outline"
                    (click)="increaseQuantity(product)"
                    [disabled]="getMaxQuantity(product) !== null && getQuantity(product) >= getMaxQuantity(product)!"
                  >
                    <svg cIcon name="cilPlus"></svg>
                  </button>
                </div>
              </div>
            </c-card-body>
          </c-card>
        </c-col>
      }
    </c-row>

    <!-- Cart Summary -->
    <c-card class="cart-summary mt-5 shadow-sm">
      <c-card-body>
        <h3 class="mb-4">{{ 'order_summary_title' | translate }}</h3>

        @if (cart().length === 0) {
          <div class="text-body-secondary text-center py-4">
            {{ 'cart_empty' | translate }}
          </div>
        } @else {
          <div class="cart-items mb-4">
            @for (item of cart(); track item.product.id) {
              <div class="cart-item d-flex justify-content-between align-items-center mb-3">
                <div>
                  <strong>{{ item.product.name }}</strong>
                  <span class="text-body-secondary ms-2">× {{ item.quantity }}</span>
                </div>
                <div class="text-end fw-bold">
                  {{ (item.product.price * item.quantity) | currencyAmount: (item.product.currency || 'USDT') }}
                </div>
              </div>
            }
          </div>

          <hr />

          <div class="cart-total d-flex justify-content-between align-items-center">
            <strong>{{ 'cart_total' | translate }}</strong>
            <strong class="text-primary fs-4">
              {{ totalPrice() | currencyAmount: cartCurrency() }}
            </strong>
          </div>

          <button
            cButton
            color="primary"
            class="w-100 mt-4"
            size="lg"
            (click)="continueToPayment()"
            [disabled]="!canContinue()"
          >
            {{ 'continue_to_payment' | translate }}
            <!-- <svg cIcon name="cilArrowRight" class="ms-2"></svg> -->
          </button>
        }
      </c-card-body>
    </c-card>
  }
</div>